import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/ui';
import { AppLayout } from '@/components/layout/AppLayout';

const ActiveCallsPage = lazy(() =>
  import('@/features/ActiveCallsPage').then(m => ({ default: m.ActiveCallsPage }))
);
const AuthPage = lazy(() => import('@/features/auth/AuthPage'));
const DashboardPage = lazy(() =>
  import('@/features/DashboardPage').then(m => ({ default: m.DashboardPage }))
);
const KeywordsManagementPage = lazy(() =>
  import('@/features/KeywordsManagementPage').then(m => ({
    default: m.KeywordsManagementPage,
  }))
);
const CallHistoryShellPage = lazy(() =>
  import('@/components/CallHistoryPage').then(m => ({
    default: m.CallHistoryShellPage,
  }))
);
const NominalsListPage = lazy(() =>
  import('@/features/userManagement/managementAccountsListPage').then(m => ({
    default: m.NominalsListPage,
  }))
);
const NominalDetailPage = lazy(() =>
  import('@/features/NominalDetailPage').then(m => ({
    default: m.NominalDetailPage,
  }))
);
const UsersListPage = lazy(() =>
  import('@/features/userManagement/managementAccountsListPage').then(m => ({
    default: m.UsersListPage,
  }))
);
const UserDetailPage = lazy(() =>
  import('@/features/UserDetailPage').then(m => ({ default: m.UserDetailPage }))
);
const AddressBookPage = lazy(() =>
  import('@/features/AddressBookPage').then(m => ({
    default: m.AddressBookPage,
  }))
);
const AccountSettingsPage = lazy(() =>
  import('@/features/AccountSettingsPage').then(m => ({
    default: m.AccountSettingsPage,
  }))
);
const EstablishmentSettingsPage = lazy(() =>
  import('@/features/account/EstablishmentSettingsPage').then(m => ({
    default: m.EstablishmentSettingsPage,
  }))
);
const VoicemailsPage = lazy(() =>
  import('@/features/VoicemailsPage').then(m => ({ default: m.VoicemailsPage }))
);

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

type ProtectedRouteProps = {
  children: React.ReactNode;
  staffOnly?: boolean;
};

// Nominals use a separate app and never reach protected frontend routes.
function ProtectedRoute({ children, staffOnly = false }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (staffOnly && user?.is_staff !== true) {
    return <Navigate to="/" replace />;
  }

  return <AppLayout isStaff={user?.is_staff === true}>{children}</AppLayout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<FullScreenSpinner />}>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/active-calls"
            element={
              <ProtectedRoute staffOnly>
                <ActiveCallsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/call-history"
            element={
              <ProtectedRoute>
                <CallHistoryShellPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominals"
            element={
              <ProtectedRoute>
                <NominalsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominals/:id"
            element={
              <ProtectedRoute>
                <NominalDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <UserDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/address-book"
            element={
              <ProtectedRoute>
                <AddressBookPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/keywords"
            element={
              <ProtectedRoute>
                <KeywordsManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account-settings"
            element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/establishment-settings"
            element={
              <ProtectedRoute staffOnly>
                <EstablishmentSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voicemails"
            element={
              <ProtectedRoute>
                <VoicemailsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
